import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FaArrowLeft,
    FaBookOpen,
    FaChalkboardTeacher,
    FaEnvelope,
    FaGraduationCap,
    FaIdBadge,
    FaUser,
    FaUserCircle,
    FaUsers,
} from "react-icons/fa";

import api from "../api/api";
import defaultAvatar from "./learningenvironment.jpg";

const BIO_STYLES = `
.cayep-personal-bio {
    --cayep-nav-navy: #062A4B;
    --cayep-nav-blue: #063A6B;
    --cayep-nav-black: #0A0F12;
    --cayep-nav-gold: #F6C944;
    --cayep-nav-yellow: #EFA81C;
    --cayep-nav-orange: #C0750D;
    --cayep-nav-green: #4FAE22;
    --cayep-nav-olive: #5F6622;
    --cayep-nav-sky: #0F6DAB;
    --cayep-nav-white: #F0EDEA;
    --cayep-nav-gray: #C4C7C7;
    --cayep-nav-antique: #CBA454;

    --nav-white: #ffffff;
    --nav-text: #142536;
    --nav-muted: #6b7280;
    --nav-border: #e7ebee;
    --nav-soft: #f7f9fa;
    --nav-shadow: 0 12px 35px rgba(6, 42, 75, 0.10);
    --nav-transition: 220ms ease;

    width: 100%;
    min-width: 0;
    animation: cayepBioFadeIn 220ms ease;
}

@keyframes cayepBioFadeIn {
    from {
        opacity: 0;
        transform: translateY(6px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.cayep-bio-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 8px 12px;
    border: 1px solid var(--nav-border);
    border-radius: 8px;
    background: var(--nav-white);
    color: var(--cayep-nav-navy);
    cursor: pointer;
    font-family: inherit;
    font-size: 11px;
    font-weight: 800;
    transition: var(--nav-transition);
}

.cayep-bio-back:hover {
    border-color: var(--cayep-nav-sky);
    color: var(--cayep-nav-sky);
    transform: translateX(-2px);
}

.cayep-bio-card {
    overflow: hidden;
    background: var(--nav-white);
    border: 1px solid var(--nav-border);
    border-radius: 14px;
    box-shadow: var(--nav-shadow);
}

.cayep-bio-cover {
    position: relative;
    min-height: 145px;
    background:
        radial-gradient(
            circle at 15% 20%,
            rgba(246, 201, 68, 0.20),
            transparent 28%
        ),
        radial-gradient(
            circle at 82% 25%,
            rgba(15, 109, 171, 0.30),
            transparent 32%
        ),
        linear-gradient(
            135deg,
            var(--cayep-nav-black),
            var(--cayep-nav-navy) 55%,
            var(--cayep-nav-blue)
        );
}

.cayep-bio-cover-pattern {
    position: absolute;
    inset: 0;
    opacity: 0.18;
    background-image:
        linear-gradient(
            45deg,
            transparent 46%,
            rgba(255,255,255,0.18) 47%,
            transparent 48%
        ),
        linear-gradient(
            -45deg,
            transparent 46%,
            rgba(255,255,255,0.12) 47%,
            transparent 48%
        );
    background-size: 28px 28px;
}

.cayep-bio-cover-label {
    position: absolute;
    right: 18px;
    bottom: 15px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 10px;
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 7px;
    background: rgba(6, 42, 75, 0.72);
    color: var(--cayep-nav-white);
    backdrop-filter: blur(6px);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.04em;
}

.cayep-bio-main {
    position: relative;
    padding: 0 22px 24px;
}

.cayep-bio-avatar-wrap {
    position: relative;
    width: 108px;
    height: 108px;
    margin-top: -54px;
    margin-bottom: 12px;
    border: 5px solid var(--nav-white);
    border-radius: 50%;
    background: var(--nav-soft);
    box-shadow: 0 8px 25px rgba(6, 42, 75, 0.16);
    overflow: hidden;
}

.cayep-bio-avatar-wrap img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
}

.cayep-bio-avatar-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--cayep-nav-sky);
    font-size: 40px;
}

.cayep-bio-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.cayep-bio-name {
    margin: 0;
    color: var(--cayep-nav-navy);
    font-size: 20px;
    line-height: 1.25;
    font-weight: 900;
}

.cayep-bio-role {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 8px;
    border-radius: 6px;
    background: #EEF5FA;
    color: var(--cayep-nav-sky);
    font-size: 9px;
    font-weight: 900;
}

.cayep-bio-role.management {
    background: #FFF3DC;
    color: var(--cayep-nav-orange);
}

.cayep-bio-username {
    margin-top: 4px;
    color: var(--nav-muted);
    font-size: 11px;
}

.cayep-bio-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 20px;
}

.cayep-bio-info {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
    padding: 12px;
    border: 1px solid var(--nav-border);
    border-radius: 9px;
    background: var(--nav-soft);
}

.cayep-bio-info-icon {
    width: 30px;
    height: 30px;
    min-width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #EEF5FA;
    color: var(--cayep-nav-sky);
    font-size: 11px;
}

.cayep-bio-info-content {
    min-width: 0;
}

.cayep-bio-info-label {
    display: block;
    margin-bottom: 3px;
    color: var(--nav-muted);
    font-size: 8px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.cayep-bio-info-value {
    display: block;
    color: var(--nav-text);
    font-size: 11px;
    font-weight: 800;
    overflow-wrap: anywhere;
}

.cayep-bio-course {
    margin-top: 10px;
}

.cayep-bio-course .cayep-bio-info-icon {
    background: #FFF5D9;
    color: var(--cayep-nav-orange);
}

.cayep-bio-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 18px;
    padding-top: 15px;
    border-top: 1px solid var(--nav-border);
    color: var(--nav-muted);
    font-size: 9px;
    line-height: 1.5;
}

.cayep-bio-footer svg {
    color: var(--cayep-nav-gold);
}

.cayep-bio-loading,
.cayep-bio-error {
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
    padding: 25px;
    border: 1px solid var(--nav-border);
    border-radius: 14px;
    background: var(--nav-white);
    color: var(--nav-muted);
    text-align: center;
}

.cayep-bio-error strong {
    color: var(--cayep-nav-navy);
    font-size: 13px;
}

.cayep-bio-error p {
    margin: 0;
    max-width: 450px;
    font-size: 10px;
    line-height: 1.6;
}

.cayep-bio-error-code {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 7px 9px;
    border-radius: 7px;
    background: #f1f5f8;
    color: var(--cayep-nav-navy);
    font-family: monospace;
    font-size: 9px;
    overflow-wrap: anywhere;
}

@media (max-width: 650px) {
    .cayep-bio-grid {
        grid-template-columns: 1fr;
    }

    .cayep-bio-main {
        padding: 0 15px 20px;
    }

    .cayep-bio-name {
        font-size: 17px;
    }

    .cayep-bio-cover {
        min-height: 125px;
    }

    .cayep-bio-cover-label {
        right: 12px;
        bottom: 12px;
        font-size: 8px;
    }
}
`;

const MANAGEMENT_ROLES = new Set([
    "teacher",
    "facilitator",
    "instructor",
    "admin",
    "administrator",
    "principal",
    "proprietor",
    "vpi",
    "vpa",
    "dean",
    "it personel",
    "business_manager",
]);

const isValidUserId = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return false;
    }

    if (
        typeof value === "number" &&
        Number.isFinite(value) &&
        value > 0
    ) {
        return true;
    }

    if (typeof value === "string") {
        return (
            value.trim() !== "" &&
            /^\d+$/.test(value.trim()) &&
            Number(value) > 0
        );
    }

    return false;
};

const resolveUserId = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (isValidUserId(value)) {
        return value;
    }

    if (typeof value !== "object") {
        return null;
    }

    const directCandidates = [
        value.id,
        value.user_id,
        value.student_id,
        value.author_id,
        value.user,
        value.student,
        value.author,
    ];

    for (const candidate of directCandidates) {
        if (isValidUserId(candidate)) {
            return candidate;
        }

        if (
            candidate &&
            typeof candidate === "object"
        ) {
            const nestedId =
                candidate.id ||
                candidate.user_id ||
                candidate.student_id ||
                candidate.author_id;

            if (isValidUserId(nestedId)) {
                return nestedId;
            }
        }
    }

    const nestedCandidates = [
        value.user?.id,
        value.user?.user_id,
        value.student?.id,
        value.student?.user_id,
        value.author?.id,
        value.author?.user_id,
    ];

    for (const candidate of nestedCandidates) {
        if (isValidUserId(candidate)) {
            return candidate;
        }
    }

    return null;
};

const StudentPersonalBio = ({
    userId,
    course,
    fallbackProfile = null,
    onBack,
}) => {
    const resolvedUserId = useMemo(() => {
        const directId = resolveUserId(userId);

        if (directId) {
            return directId;
        }

        return resolveUserId(fallbackProfile);
    }, [
        userId,
        fallbackProfile,
    ]);

    const [profile, setProfile] = useState(
        fallbackProfile || null
    );

    const [loading, setLoading] = useState(
        Boolean(resolvedUserId)
    );

    const [error, setError] = useState("");

    const courseId = course?.id;

    const getErrorMessage = useCallback(
        (requestError) => {
            const data =
                requestError?.response?.data;

            if (typeof data === "string") {
                return data;
            }

            if (data?.detail) {
                return String(data.detail);
            }

            if (data?.message) {
                return String(data.message);
            }

            const status =
                requestError?.response?.status;

            if (status === 400) {
                return "The profile request contains invalid information.";
            }

            if (status === 401) {
                return "Your session has expired. Please sign in again.";
            }

            if (status === 403) {
                return "You do not have permission to view this community profile.";
            }

            if (status === 404) {
                return "This community member could not be found in the selected course.";
            }

            return "The selected profile could not be loaded.";
        },
        []
    );

    useEffect(() => {
        let active = true;

        const loadProfile = async () => {
            if (!courseId) {
                if (active) {
                    setLoading(false);
                    setError(
                        "No course was provided for this profile."
                    );
                }

                return;
            }

            if (!resolvedUserId) {
                if (active) {
                    setLoading(false);
                    setError(
                        "This community member does not have a valid User ID."
                    );
                }

                return;
            }

            setLoading(true);
            setError("");

            try {
                const response = await api.get(
                    `api/users/subjects/${courseId}/community-profile/${resolvedUserId}/`
                );

                if (!active) {
                    return;
                }

                const responseProfile =
                    response?.data || null;

                if (!responseProfile) {
                    setProfile(null);
                    setError(
                        "The community profile returned no profile information."
                    );
                    return;
                }

                setProfile(responseProfile);
            } catch (requestError) {
                console.error(
                    "Unable to load community profile:",
                    requestError
                );

                if (!active) {
                    return;
                }

                setError(
                    getErrorMessage(requestError)
                );
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadProfile();

        return () => {
            active = false;
        };
    }, [
        resolvedUserId,
        courseId,
        getErrorMessage,
    ]);

    const normalizedRole = useMemo(() => {
        return String(
            profile?.role ||
                profile?.user_role ||
                profile?.user_type ||
                "student"
        )
            .trim()
            .toLowerCase();
    }, [profile]);

    const isManagement =
        MANAGEMENT_ROLES.has(normalizedRole);

    const displayName =
        profile?.name ||
        profile?.full_name ||
        profile?.display_name ||
        profile?.username ||
        profile?.email ||
        "Community Member";

    const username =
        profile?.username ||
        profile?.user_username ||
        "";

    const email =
        profile?.email ||
        profile?.user_email ||
        "";

    const avatar =
        profile?.avatar ||
        profile?.profile_image ||
        profile?.profile_picture ||
        defaultAvatar;

    const courseName =
        profile?.course_name ||
        profile?.course?.name ||
        profile?.course?.title ||
        course?.name ||
        course?.title ||
        "Current Course";

    const roleLabel =
        profile?.role ||
        profile?.user_role ||
        profile?.user_type ||
        "Student";

    return (
        <>
            <style>{BIO_STYLES}</style>

            <section className="cayep-personal-bio">
                <button
                    type="button"
                    className="cayep-bio-back"
                    onClick={onBack}
                >
                    <FaArrowLeft />
                    Back to Question Community
                </button>

                {loading ? (
                    <div className="cayep-bio-loading">
                        <FaUserCircle size={28} />

                        <strong>
                            Loading community profile...
                        </strong>

                        <span className="cayep-bio-error-code">
                            User ID: {String(resolvedUserId)}
                        </span>
                    </div>
                ) : error ? (
                    <div className="cayep-bio-error">
                        <FaUserCircle size={30} />

                        <strong>
                            Profile Unavailable
                        </strong>

                        <p>{error}</p>

                        {resolvedUserId && (
                            <span className="cayep-bio-error-code">
                                User ID:{" "}
                                {String(
                                    resolvedUserId
                                )}
                            </span>
                        )}

                        <button
                            type="button"
                            className="cayep-bio-back"
                            onClick={onBack}
                        >
                            <FaArrowLeft />
                            Back to Question Community
                        </button>
                    </div>
                ) : (
                    <div className="cayep-bio-card">
                        <div className="cayep-bio-cover">
                            <div className="cayep-bio-cover-pattern" />

                            <div className="cayep-bio-cover-label">
                                <FaUsers />
                                Course Community Profile
                            </div>
                        </div>

                        <div className="cayep-bio-main">
                            <div className="cayep-bio-avatar-wrap">
                                {avatar ? (
                                    <img
                                        src={avatar}
                                        alt={displayName}
                                        onError={(
                                            event
                                        ) => {
                                            if (
                                                event
                                                    .currentTarget
                                                    .src !==
                                                defaultAvatar
                                            ) {
                                                event.currentTarget.src =
                                                    defaultAvatar;
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="cayep-bio-avatar-fallback">
                                        <FaUserCircle />
                                    </div>
                                )}
                            </div>

                            <div className="cayep-bio-name-row">
                                <h2 className="cayep-bio-name">
                                    {displayName}
                                </h2>

                                <span
                                    className={`cayep-bio-role ${
                                        isManagement
                                            ? "management"
                                            : ""
                                    }`}
                                >
                                    {isManagement ? (
                                        <FaChalkboardTeacher />
                                    ) : (
                                        <FaGraduationCap />
                                    )}

                                    {roleLabel}
                                </span>
                            </div>

                            {username && (
                                <div className="cayep-bio-username">
                                    @{username}
                                </div>
                            )}

                            <div className="cayep-bio-grid">
                                <div className="cayep-bio-info">
                                    <div className="cayep-bio-info-icon">
                                        <FaIdBadge />
                                    </div>

                                    <div className="cayep-bio-info-content">
                                        <span className="cayep-bio-info-label">
                                            Username
                                        </span>

                                        <span className="cayep-bio-info-value">
                                            {username ||
                                                "Not available"}
                                        </span>
                                    </div>
                                </div>

                                <div className="cayep-bio-info">
                                    <div className="cayep-bio-info-icon">
                                        <FaEnvelope />
                                    </div>

                                    <div className="cayep-bio-info-content">
                                        <span className="cayep-bio-info-label">
                                            Email
                                        </span>

                                        <span className="cayep-bio-info-value">
                                            {email ||
                                                "Not available"}
                                        </span>
                                    </div>
                                </div>

                                <div className="cayep-bio-info">
                                    <div className="cayep-bio-info-icon">
                                        {isManagement ? (
                                            <FaChalkboardTeacher />
                                        ) : (
                                            <FaUser />
                                        )}
                                    </div>

                                    <div className="cayep-bio-info-content">
                                        <span className="cayep-bio-info-label">
                                            Role
                                        </span>

                                        <span className="cayep-bio-info-value">
                                            {roleLabel}
                                        </span>
                                    </div>
                                </div>

                                <div className="cayep-bio-info cayep-bio-course">
                                    <div className="cayep-bio-info-icon">
                                        <FaBookOpen />
                                    </div>

                                    <div className="cayep-bio-info-content">
                                        <span className="cayep-bio-info-label">
                                            Course
                                        </span>

                                        <span className="cayep-bio-info-value">
                                            {courseName}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="cayep-bio-footer">
                                <FaGraduationCap />

                                <span>
                               power by: POLU - INTEGRATED TECHONOLOGY
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default StudentPersonalBio;

